// Ready.net Integration Roadmap.
//
// This page is the single best artifact for an actual Ready.net partner
// meeting. It's structured so you can navigate to it during a screen-
// share, walk Ready.net through what we plan to build with them, and
// land the asks (POPS schema, sandbox tenant, technical contact).
//
// Each integration card shows:
//   - Status (live / ready to ship / awaiting Ready.net spec / planned)
//   - Which Ready.net product it feeds (CPC / APP / Performance Test / ARC)
//   - What data Milestone Build emits
//   - The format / spec it conforms to
//   - What's needed from Ready.net to ship



type IntegrationStatus = 'live' | 'ready_to_ship' | 'needs_spec' | 'awaiting_core' | 'planned';

type Integration = {
  id: string;
  name: string;
  destination: string;       // which Ready.net product
  destinationUrl?: string;
  description: string;
  emits: string;             // what data we emit
  format: string;            // format/spec it conforms to
  formatUrl?: string;
  status: IntegrationStatus;
  needsFromReadyNet?: string;
  ourPiecesReady: string[];  // bullets of what we already have on our side
};

const STATUS_BADGE: Record<IntegrationStatus, { bg: string; fg: string; label: string }> = {
  live:           { bg: '#d1fae5', fg: '#065f46', label: '● Live' },
  ready_to_ship:  { bg: '#dbeafe', fg: '#1e40af', label: '◇ Ready to ship' },
  needs_spec:     { bg: '#fef3c7', fg: '#92400e', label: '◬ Needs Ready.net spec' },
  awaiting_core:  { bg: '#fce7f3', fg: '#9d174d', label: '◬ Awaiting Build↔Core integration' },
  planned:        { bg: '#f1f5f9', fg: '#475569', label: '○ Planned' },
};

const INTEGRATIONS: Integration[] = [
  {
    id: 'lcso-enrollment',
    name: 'LCSO Enrollment & Eligibility',
    destination: 'Ready.net ARC (Proof of Marketing)',
    description: 'Per-customer LCSO enrollment record with eligibility verification (SNAP / Medicaid / WIC / etc.) and date.',
    emits: 'CSV: location_id, subscriber_id, enrolled_date, tier, eligibility_basis, verified_at',
    format: 'ARC Proof of Marketing — LCSO subset (not yet published)',
    status: 'awaiting_core',
    needsFromReadyNet: 'LCSO enrollment ingestion schema',
    ourPiecesReady: [
      'SVC-LCSO-ENROLL obligation (quarterly cadence) in catalog',
      'SVC-LCSO-COMP obligation (annual reasonable comparability)',
      'Per-cadence tracking + document upload',
      "Build↔Core integration spec scaffolded (POST /core/lcso-enrollment)",
    ],
  },
  {
    id: 'lcso-marketing',
    name: 'LCSO Marketing Campaign Evidence',
    destination: 'Ready.net ARC (Proof of Marketing)',
    description: 'Per-campaign records of LCSO marketing activity: door-to-door, direct mail, digital, paid media. Each campaign carries channel, dates, reach, cost.',
    emits: 'JSON array of campaigns with channel, dates, reach metrics, conversion, evidence document URI',
    format: 'ARC Proof of Marketing format (not yet published)',
    status: 'awaiting_core',
    needsFromReadyNet: 'Proof of Marketing schema',
    ourPiecesReady: [
      'SVC-LCSO-MKT obligation (quarterly cadence)',
      'OUT-CANV (door-to-door canvassing) obligation (continuous)',
      'OUT-PAID (paid media) obligation',
      'Build↔Core integration spec scaffolded (POST /core/lcso-marketing + /core/canvassing)',
    ],
  },
  {
    id: 'fcc-labels',
    name: 'FCC Broadband Nutrition Labels',
    destination: 'Ready.net ARC (per-tier disclosure)',
    description: 'Per-service-tier FCC Broadband Nutrition Label PDFs. Required to be displayed at point of sale (47 CFR §8.1).',
    emits: 'PDF per tier + structured tier metadata (download/upload speed, monthly price, fees, terms)',
    format: 'FCC Broadband Label format (per 47 CFR §8.1) + ARC ingestion convention',
    status: 'awaiting_core',
    needsFromReadyNet: 'ARC ingestion convention for FCC label PDFs',
    ourPiecesReady: [
      'SVC-LABEL obligation (continuous)',
      'SVC-PRICE obligation (continuous)',
      'Document store ready to receive PDFs from Core',
      "Build↔Core fetcher spec scaffolded (GET core://broadband-labels)",
    ],
  },
  {
    id: 'subscriber-ledger',
    name: 'Subscriber Activation Ledger',
    destination: "Ready.net ARC (drives POPS quality + state quarterly)",
    description: 'Per-BSL activation record: when each subscriber went live, what tier they bought, current status. Drives the quality field of POPS and the take-rate field of state quarterly reports.',
    emits: 'CSV: fcc_location_id, first_subscriber_date, current_tier, status, activated_by_crew_id',
    format: 'ARC subscriber activation ingestion (not yet published)',
    status: 'awaiting_core',
    needsFromReadyNet: 'Subscriber-activation row schema',
    ourPiecesReady: [
      'STA-Q-SUB obligation (subscriber count + take rate, quarterly)',
      'bead_location.first_subscriber_date already in schema',
      'bead_location.served_status already in schema',
      "Build↔Core integration spec scaffolded (POST /core/subscribers + webhook on activation)",
    ],
  },
  {
    id: 'sla-metrics',
    name: 'Service Quality / SLA Metrics',
    destination: 'Ready.net ARC + state quarterly reports',
    description: 'Quarterly aggregate of customer service performance: ticket volume, time-to-first-response, MTTR, uptime aggregates.',
    emits: 'JSON object: avg/p50/p95 response and resolution times, uptime %, ticket counts',
    format: 'ARC SLA ingestion + state quarterly report shape (not yet published)',
    status: 'awaiting_core',
    needsFromReadyNet: 'SLA reporting schema',
    ourPiecesReady: [
      'SVC-SLA obligation (quarterly)',
      'STA-Q-SQ obligation (state quarterly service quality)',
      "Build↔Core integration spec scaffolded (POST /core/sla-metrics quarterly)",
    ],
  },
  {
    id: 'complaints',
    name: 'Complaint Resolution Logs',
    destination: 'Ready.net ARC + state quarterly reports',
    description: 'Per-quarter complaint ledger: ticket_id, opened_at, resolved_at, resolution_hours, category, in-SLA flag.',
    emits: 'CSV: ticket_id, opened_at, resolved_at, resolution_hours, category, resolved_in_sla',
    format: 'ARC complaints ingestion (not yet published)',
    status: 'awaiting_core',
    needsFromReadyNet: 'Complaint-log schema',
    ourPiecesReady: [
      'SVC-COMP obligation (quarterly)',
      "Build↔Core integration spec scaffolded (POST /core/complaints quarterly)",
    ],
  },
  {
    id: 'outages',
    name: 'Network Outage Notifications',
    destination: "State portals (per-state thresholds)",
    description: 'Auto-fired notification when a network outage crosses the state-defined threshold (Colorado: ≥25% of project BSLs / ≥8 hours → notify state in 15 days). State thresholds vary.',
    emits: 'JSON event: started_at, affected_locations[], severity, duration · plus auto-generated state-portal notification PDF',
    format: 'State-specific notification format',
    status: 'awaiting_core',
    needsFromReadyNet: 'Per-state notification format conventions',
    ourPiecesReady: [
      'SVC-OUTAGE-NOTIFY obligation just added to catalog',
      "Build↔Core integration spec scaffolded (webhook on outage start; auto-clock 15-day deadline)",
    ],
  },
  {
    id: 'challenge-rebuttal',
    name: 'NTIA BEAD Challenge & Rebuttal API',
    destination: 'Ready.net CPC (Challenge Process Coordinator)',
    destinationUrl: 'https://ready.net/products/challenge-process-coordinator',
    description: 'ISP submits availability rebuttal evidence (≥80% of target up/down) plus speed test results during the state\'s challenge window.',
    emits: 'Per-BSL rebuttal payload + speed test bundle (download/upload/latency/timestamp/geolocation/BSL link)',
    format: 'NTIA BEAD Challenge & Rebuttal API (public spec)',
    formatUrl: 'https://broadbandusa.ntia.gov/sites/default/files/2023-09/BEAD_Challenge_and_Rebuttal_API.pdf',
    status: 'planned',
    needsFromReadyNet: 'Re-challenge / amendment window participation — currently most state windows have closed post-award',
    ourPiecesReady: [
      'bead_location with FCC location_id, served_status',
      'Per-BSL service-ready evidence schema',
      'Document upload pipeline (PDF/CSV affidavits)',
      'Audit-logged submission with actor + timestamp',
    ],
  },
  {
    id: 'performance-measures',
    name: 'NTIA BEAD Performance Measures',
    destination: 'Ready.net Performance Test & Survey',
    destinationUrl: 'https://ready.net/products/performance-test-and-survey',
    description: 'ISP submits the post-award performance measure evidence: 80% of subscribers must hit 80% of contracted speed; latency 95% ≤ 100 ms RTT; uptime ≥ 99.45%; sampled tier (5% / 10% / 50% based on subscriber count).',
    emits: 'Time-series speed/latency/packet-loss/uptime per tested BSL, FCC-IXP measurement, one-week annual sampling window 6pm–midnight local',
    format: 'NTIA BEAD Performance Measures Policy Notice',
    formatUrl: 'https://broadbandusa.ntia.gov/sites/default/files/2025-09/Performance_Measures_Policy_Notice.pdf',
    status: 'ready_to_ship',
    ourPiecesReady: [
      'bead_location population stratification by service tier',
      'BMC speed-test methodology compatibility',
      'Per-test geolocation + timestamp logging',
      'Sample-tier auto-selection logic',
    ],
  },
  {
    id: 'sar-2',
    name: 'NTIA SAR 2.0 Semi-Annual Report',
    destination: 'Ready.net ARC',
    destinationUrl: 'https://ready.net/automated-reporting-and-compliance',
    description: 'Semi-annual report at five levels: Eligible Entity / Subgrantee / Project / Contractor / Location. Required gate to continued reimbursement.',
    emits: 'Five-level row sets per the published NTIA SAR 2.0 schema',
    format: 'NTIA SAR 2.0 Anticipated Reporting Requirements Guidance',
    formatUrl: 'https://broadbandusa.ntia.gov/sites/default/files/2024-12/BEAD_Anticipated_SAR_Reporting_Requirements_Guidance.pdf',
    status: 'ready_to_ship',
    ourPiecesReady: [
      'Project + subgrantee + contractor entities in DB',
      'bead_vendor with UEI, SAM.gov status',
      'procurement_event with awarded contractor linkage',
      'bead_location for the Location-level rows',
    ],
  },
  {
    id: 'pops',
    name: 'POPS — Proof of Passings',
    destination: 'Ready.net ARC (Proof of Passings Dashboard)',
    destinationUrl: 'https://ready.net/automated-reporting-and-compliance',
    description: 'Geospatial attestation per BSL: availability, quality, affordability. The unit of truth in BEAD post-award. ARC tethers directly to the operator system of record for these.',
    emits: 'Per-BSL JSON object: location_id, availability flag, quality (speed tier), affordability (LCSO offered), service_ready_date, geotagged photo URI, attesting actor',
    format: 'Ready.net POPS — Proof of Passings Standard (open spec, schema not yet published publicly)',
    formatUrl: 'https://ready.net/blog/automate-bead-reporting-compliance',
    status: 'needs_spec',
    needsFromReadyNet: 'POPS field-level schema documentation',
    ourPiecesReady: [
      'bead_location with all candidate POPS fields (FCC ID, served_status, service_ready_date, federal/match $)',
      'bead_evidence with geotag for per-location photo evidence',
      'Document pipeline — every photo SHA-256\'d + audit-logged',
      'Best-guess POPS-shaped emitter (will swap to your spec when available)',
    ],
  },
  {
    id: 'proof-of-labor',
    name: 'Proof of Labor — Davis-Bacon weekly payroll',
    destination: 'Ready.net ARC (Proof of Labor module)',
    destinationUrl: 'https://ready.net/automated-reporting-and-compliance',
    description: 'Weekly Davis-Bacon certified payroll evidence per 29 CFR Part 5. Required throughout construction.',
    emits: 'WH-347-shaped weekly payroll: contractor, week-ending date, employees, classifications, hours, rates, fringes, deductions',
    format: 'WH-347 (federal form) — ARC ingestion format not yet published',
    status: 'needs_spec',
    needsFromReadyNet: 'Proof of Labor ingestion format spec (or confirmation that WH-347 PDF + structured CSV companion is acceptable)',
    ourPiecesReady: [
      'WH-347 page already in product (Davis-Bacon section)',
      'FED-LAB-WH347 obligation in catalog (weekly cadence)',
      'Vendor + contractor registry with E-Verify/Title VI flags',
      'Apprenticeship ratio tracking (FED-LAB-APP)',
    ],
  },
  {
    id: 'proof-of-marketing',
    name: 'Proof of Marketing — LCSO outreach',
    destination: 'Ready.net ARC',
    destinationUrl: 'https://ready.net/automated-reporting-and-compliance',
    description: 'Evidence that the ISP offered the Low-Cost Service Option to qualifying customers per NTIA NOFO §IV.C.1.b.',
    emits: 'Marketing campaign evidence: print/digital/door-to-door outreach logs, enrollment counts, eligible-customer counts',
    format: 'ARC Proof of Marketing format (not yet published)',
    status: 'needs_spec',
    needsFromReadyNet: 'Proof of Marketing schema',
    ourPiecesReady: [
      'SVC-LCSO-OFFER, SVC-LCSO-MKT, SVC-LCSO-ENROLL, SVC-LCSO-COMP obligations in catalog',
      'Per-cadence (continuous / quarterly / annual) tracking',
      'Document attachment per marketing artifact',
    ],
  },
  {
    id: 'baba-evidence',
    name: 'BABA Evidence Packet',
    destination: 'Ready.net ARC (per milestone gate)',
    destinationUrl: 'https://ready.net/blog/what-every-state-and-territory-must-know-about-subgrantee-agreements',
    description: 'Build America Buy America certifications, supply-chain records, and waivers per shipment / per milestone.',
    emits: 'Per-shipment packet: vendor, material code, manufacturer self-cert PDF, country of origin, DFARS compliance flag, waiver references',
    format: 'ARC BABA evidence format (not yet published)',
    status: 'needs_spec',
    needsFromReadyNet: 'BABA evidence packet schema',
    ourPiecesReady: [
      'BABA Ledger page with cert lifecycle (active / expiring < 30d / expired)',
      'bead_baba_cert linked to vendor + material + bead_document PDF',
      'FED-MAT-BABA, FED-MAT-WAIVER, FED-MAT-SUPPLY (Sec.889) obligations in catalog',
      'Waiver workflow + manufacturer self-cert tracking',
    ],
  },
  {
    id: 'nepa-section-106',
    name: 'NEPA / Section 106 / Tribal Consultation',
    destination: 'Ready.net ARC (one-time pre-construction)',
    description: 'Environmental review status: NEPA categorical exclusion / EA / EIS, Section 106 consultation, tribal consultation, ESA, USACE §404, floodplain, MBTA, Clean Air Act conformity.',
    emits: 'Per-project status object + cleared dates + document attachments per category',
    format: 'ARC NEPA/§106 ingestion format (not yet published)',
    status: 'needs_spec',
    needsFromReadyNet: 'Environmental clearance ingestion schema',
    ourPiecesReady: [
      'EHP / NEPA page with 9 environmental obligations',
      'permit_crossings tracking (railroad/water/highway/interstate/bridge) with status workflow',
      'FED-ENV-NEPA, FED-ENV-S106, FED-ENV-TRIBE, FED-ENV-ESA, FED-ENV-USACE, FED-ENV-FLOOD, FED-ENV-COASTAL, FED-ENV-MBTA, FED-ENV-AIR obligations',
      'Per-permit document upload with status workflow',
    ],
  },
  {
    id: 'match-contributions',
    name: 'Match Contributions Ledger',
    destination: 'Ready.net ARC (per-draw evidence)',
    description: 'Cash + in-kind match documentation accompanying each reimbursement request. Required by 2 CFR 200.306.',
    emits: 'Per-draw match ledger: date, amount, source, type (cash/in-kind), supporting document URI',
    format: 'ARC match documentation format (not yet published)',
    status: 'needs_spec',
    needsFromReadyNet: 'Match ledger ingestion format',
    ourPiecesReady: [
      'FED-FIN-MATCH obligation (per_draw cadence)',
      'Match share tracking on every project',
      'Document linkage per draw via Draws page',
    ],
  },
  {
    id: 'reimbursement-bundle',
    name: 'Reimbursement Request Bundle',
    destination: 'Ready.net ARC (milestone draws)',
    description: 'Milestone-driven draw request with all supporting evidence rolled into one bundle.',
    emits: 'Milestone identifier + draw % + federal $ + match $ + linked obligations + attached evidence URIs',
    format: 'ARC reimbursement format (not yet published)',
    status: 'needs_spec',
    needsFromReadyNet: 'Reimbursement bundle schema + milestone identifier convention',
    ourPiecesReady: [
      'bead_milestone with draw_pct, target_date, completed_date, status workflow',
      'Draws page with per-milestone editor + SF-425 attachment',
      'Federal/match split at award + per-milestone level',
    ],
  },
];

const READY_NET_PRODUCTS = [
  { name: 'CPC',   full: 'Challenge Process Coordinator', stage: 'Pre-award' },
  { name: 'Perf',  full: 'Performance Test & Survey',     stage: 'Cross-cycle' },
  { name: 'ARC',   full: 'Automated Reporting & Compliance', stage: 'Post-award' },
];

function StatusBadge({ status }: { status: IntegrationStatus }) {
  const s = STATUS_BADGE[status];
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '3px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--border, #e2e8f0)',
      borderTop: `3px solid ${color ?? '#3b82f6'}`,
      borderRadius: 6, padding: '12px 14px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.06em', color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function ReadyNetIntegrationPage() {
  const counts = INTEGRATIONS.reduce((acc, i) => {
    acc[i.status] = (acc[i.status] ?? 0) + 1;
    return acc;
  }, {} as Record<IntegrationStatus, number>);

  return (
    <div>
      <div className="page__header" style={{ marginBottom: 16 }}>
        <h1 className="page__title" style={{ margin: 0, fontSize: 24 }}>Ready.net Integration</h1>
        <div className="page__subtitle" style={{ color: '#64748b', fontSize: 13 }}>
          How Milestone Build feeds Ready.net's post-award compliance and reporting products. Roadmap, status, and partnership asks.
        </div>
      </div>

      {/* Hero positioning */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a, #1e3a8a)',
        color: '#fff', padding: 24, borderRadius: 8, marginBottom: 16,
      }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.12em', opacity: 0.7 }}>How we fit</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
          Ready.net runs the state portal. Milestone Build runs the ISP side.
        </div>
        <div style={{ fontSize: 13, marginTop: 8, opacity: 0.92 }}>
          ISPs use Milestone Build to manage federal compliance and capture per-BSL evidence
          throughout construction and reporting. We push that data into the state portal their
          grant flows through — Ready.net's Performance Test & Survey and ARC.
          Same project, different sides of the table.
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          {READY_NET_PRODUCTS.map(p => (
            <div key={p.name} style={{
              background: 'rgba(255,255,255,0.08)', padding: '6px 12px', borderRadius: 6,
              fontSize: 12,
            }}>
              <span style={{ fontWeight: 700 }}>{p.name}</span>
              <span style={{ opacity: 0.7, marginLeft: 6 }}>{p.full}</span>
              <span style={{ opacity: 0.5, marginLeft: 6 }}>· {p.stage}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Status strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <StatCard label="Live" value={counts.live ?? 0} color="#10b981" sub="In production" />
        <StatCard label="Ready to ship" value={counts.ready_to_ship ?? 0} color="#3b82f6" sub="Public NTIA specs — implementable today" />
        <StatCard label="Awaiting Ready.net spec" value={counts.needs_spec ?? 0} color="#f59e0b" sub="Need format docs from partner" />
        <StatCard label="Planned" value={counts.planned ?? 0} color="#94a3b8" sub="On roadmap" />
      </div>

      {/* What we need from Ready.net */}
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>What we'd need from Ready.net to ship the integration</h2>
        <div style={{
          background: '#fff', border: '1px solid var(--border)', borderRadius: 6, padding: 16, fontSize: 13,
        }}>
          <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
            <li><b>POPS schema documentation.</b> Field-level spec for the Proof of Passings Standard. Even a draft is fine — it unblocks the most-used ARC ingestion path.</li>
            <li><b>Sandbox ARC tenant.</b> Test instance we can push payloads into without affecting any real state customer.</li>
            <li><b>A technical contact</b> (engineer or solutions architect) for the integration build.</li>
            <li><b>Schema definitions for the other ARC ingestion categories</b> as Ready.net stands them up: Proof of Labor, Proof of Marketing, BABA evidence, NEPA / §106, match documentation, reimbursement bundles.</li>
            <li><b>Listed integration partner status</b> on the Ready.net website / partner page.</li>
            <li><b>Joint introductions to 1–2 State Broadband Offices</b> whose ISP awardees we could pilot with.</li>
          </ol>
          <div style={{ marginTop: 12, padding: 10, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, fontSize: 12 }}>
            <b>Explicitly NOT asking for:</b> exclusivity, revenue share, joint product development. We sell to ISPs. You sell to states. Different customers — we make your product more valuable to your state customers without any commercial overlap.
          </div>
        </div>
      </section>

      {/* Roadmap cards */}
      <section>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Integration roadmap · {INTEGRATIONS.length} planned emitters</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {INTEGRATIONS.map((i, idx) => (
            <div key={i.id} style={{
              background: '#fff', border: '1px solid var(--border)', borderRadius: 6, padding: 16,
              display: 'grid', gridTemplateColumns: '36px 1fr 200px', gap: 16,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: '#f1f5f9', color: '#64748b',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700,
              }}>{idx + 1}</div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                  <h3 style={{ margin: 0, fontSize: 15 }}>{i.name}</h3>
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                  → {i.destinationUrl ? <a href={i.destinationUrl} target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>{i.destination}</a> : i.destination}
                </div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>{i.description}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>What we emit</div>
                    <div style={{ color: '#0f172a' }}>{i.emits}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Format / spec</div>
                    <div style={{ color: '#0f172a' }}>
                      {i.formatUrl ? <a href={i.formatUrl} target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>{i.format}</a> : i.format}
                    </div>
                  </div>
                </div>
                {i.needsFromReadyNet && (
                  <div style={{ marginTop: 10, padding: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, fontSize: 12 }}>
                    <span style={{ fontWeight: 700, color: '#92400e' }}>Need from Ready.net: </span>
                    <span style={{ color: '#0f172a' }}>{i.needsFromReadyNet}</span>
                  </div>
                )}
                <details style={{ marginTop: 8, fontSize: 12 }}>
                  <summary style={{ cursor: 'pointer', color: '#3b82f6', fontWeight: 600 }}>
                    What we already have on our side ({i.ourPiecesReady.length})
                  </summary>
                  <ul style={{ margin: '6px 0 0 20px', padding: 0, color: '#0f172a' }}>
                    {i.ourPiecesReady.map((p, j) => <li key={j} style={{ marginBottom: 2 }}>{p}</li>)}
                  </ul>
                </details>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                <StatusBadge status={i.status} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer ask */}
      <div style={{
        marginTop: 24, padding: 20, background: '#0f172a', color: '#fff',
        borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Ready to start the integration conversation.</div>
          <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
            3 emitters can ship today against public NTIA specs. The other 7 ship in 2–3 weeks each once we have your schema.
          </div>
        </div>
      </div>
    </div>
  );
}
